import { Fragment } from 'react'
import { fbt } from 'fbtee'
import { cn, compareByDisplayName } from '../lib/utils'
import { timeDurationMin } from '../lib/formatDate'
import { useFavoriteIdSet } from '../contexts/FavoriteFriendsContext'
import { useTransitionNavigate } from '../lib/viewTransition'
import { InterestBadge } from './InterestBadge'
import { PregameGoerIcon } from './PregameStrip'
import { TicketHolderIcon } from './TicketHolderIcon'
import { TotemAvatar } from './TotemAvatar'
import { UserRow } from './UserRow'
import { PartialRangeBadge, PartialRangeSlider, PartialRangeTimeline, PartialRangeTrigger } from './PartialRangeControl'
import { usePartialRangeEditor } from '../hooks/usePartialRangeEditor'
import type { PartialRange, TotemBorderStyle, UserProfile } from '../types'
import type { TicketMarks } from '../hooks/useTicketMarksByEvent'
import { clickableProps } from '../lib/a11y'

export interface TotemDisplay {
  emoji: string
  name: string
  imageUrl?: string
  borderStyle?: TotemBorderStyle
  partialRange?: PartialRange
  groupId?: string
  totemId?: string
  /** Viewer may adjust this totem's partial range at the set. */
  canEdit?: boolean
}

interface Props {
  interestedMemberIds: string[]
  goingMemberIds: string[]
  /** Given a ticket they haven't confirmed — listed between going and interested, badged only if they marked interest. */
  tentativeIds?: string[]
  profiles: Record<string, UserProfile>
  currentUserId?: string | null
  totems?: TotemDisplay[]
  setStartTime?: string
  setEndTime?: string
  onUpdatePartialRange?: (
    groupId: string,
    totemId: string,
    partialRange: PartialRange | null,
  ) => void
  /** The current user's own slice of this set, editable inline on their row. */
  ownPartialRange?: PartialRange | null
  onUpdateOwnRange?: (partialRange: PartialRange | null) => void
  /** Other members' slices, shown read-only beside their name. */
  memberRanges?: Record<string, PartialRange>
  /** Festival mode: people (you included) checked in at this set — double check, sorted first. */
  checkedInIds?: ReadonlySet<string>
  /** People going to a pre-game the viewer can see; their rows get a 🍸. Pre-game-only goers are listed last, badge-free. */
  pregameGoerIds?: ReadonlySet<string>
  /** Who holds (or was given, unconfirmed) a ticket; their rows get the ticket mark. */
  tickets?: TicketMarks
  /** Opens the totem's route view. Festival mode only — omitted elsewhere. */
  onOpenTotem?: (groupId: string, totemId: string) => void
  /** Opens another member's day view. Festival mode only — elsewhere rows go to /users/:uid. */
  onOpenMember?: (uid: string) => void
  className?: string
}

function TotemRow({
  totem: t,
  setStartTime,
  setEndTime,
  onUpdatePartialRange,
  onOpenProfile,
}: {
  totem: TotemDisplay
  setStartTime?: string
  setEndTime?: string
  onUpdatePartialRange?: (
    groupId: string,
    totemId: string,
    partialRange: PartialRange | null,
  ) => void
  onOpenProfile?: () => void
}) {
  const canEdit = !!(t.canEdit && t.groupId && t.totemId && setStartTime && setEndTime && onUpdatePartialRange)
  const duration = setStartTime && setEndTime ? timeDurationMin(setStartTime, setEndTime) : 0
  const editor = usePartialRangeEditor({
    range: t.partialRange,
    duration,
    onChange: range => onUpdatePartialRange!(t.groupId!, t.totemId!, range),
  })

  return (
    <li className="flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <div
          className={cn('flex items-center gap-2.5 min-w-0', onOpenProfile && 'cursor-pointer')}
          {...clickableProps(onOpenProfile)}
        >
          <TotemAvatar
            emoji={t.emoji}
            imageUrl={t.imageUrl}
            borderStyle={t.borderStyle}
            partial={editor.hasRange}
            className="size-8 text-base"
          />
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm truncate">{t.name}</span>
            </div>
          </div>
        </div>
        <div className="shrink-0">
          {setStartTime && (
            <PartialRangeTrigger editor={editor} setStartTime={setStartTime} duration={duration} canEdit={canEdit} />
          )}
        </div>
      </div>
      {editor.expanded && canEdit && setStartTime && setEndTime && (
        <PartialRangeSlider
          editor={editor}
          duration={duration}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
        />
      )}
    </li>
  )
}

/**
 * One person in the list. The current user's row doubles as the entry point for
 * setting their own slice of the set — the same control the totem row uses, so
 * "when will the totem be there" and "when will I be there" read identically.
 */
function MemberRow({
  uid,
  displayName,
  profile,
  status,
  isYou,
  favorite,
  checkedIn,
  pregameGoer,
  ticket,
  range,
  setStartTime,
  setEndTime,
  onUpdateOwnRange,
  onClick,
}: {
  uid: string
  displayName: string
  profile?: UserProfile
  status: 'going' | 'interested' | null
  isYou: boolean
  favorite: boolean
  checkedIn: boolean
  pregameGoer?: boolean
  ticket?: 'holder' | 'pending'
  range?: PartialRange
  setStartTime?: string
  setEndTime?: string
  onUpdateOwnRange?: (range: PartialRange | null) => void
  onClick: () => void
}) {
  // Only a `going` mark can carry a range: it is a commitment, and setting one
  // promotes the doc. Offering the control on an `interested` row would hide
  // that promotion behind a slider.
  const canEdit = !!(isYou && status === 'going' && onUpdateOwnRange && setStartTime && setEndTime)
  const duration = setStartTime && setEndTime ? timeDurationMin(setStartTime, setEndTime) : 0
  const editor = usePartialRangeEditor({
    range,
    duration,
    onChange: r => onUpdateOwnRange!(r),
  })

  return (
    <Fragment key={uid}>
      <UserRow
        name={displayName}
        profile={profile}
        isYou={isYou}
        favorite={favorite}
        // A time badge already means going — a range can only sit on a going
        // mark — so the two never both appear. That keeps every row to one
        // trailing element, which is what leaves the name room to breathe.
        action={
          <span className="flex items-center gap-1.5">
            {pregameGoer && <PregameGoerIcon />}
            {ticket && <TicketHolderIcon pending={ticket === 'pending'} />}
            {canEdit && setStartTime ? (
              <PartialRangeTrigger editor={editor} setStartTime={setStartTime} duration={duration} canEdit checkedIn={checkedIn} />
            ) : range && setStartTime && duration > 0 ? (
              <PartialRangeTimeline
                setStartTime={setStartTime}
                duration={duration}
                startMin={range.startMin}
                endMin={range.endMin}
                checkedIn={checkedIn}
              />
            ) : range && setStartTime ? (
              <PartialRangeBadge
                setStartTime={setStartTime}
                startMin={range.startMin}
                endMin={range.endMin}
                checkedIn={checkedIn}
              />
            ) : status && (
              <InterestBadge status={status} checkedIn={checkedIn} />
            )}
          </span>
        }
        onClick={onClick}
      />
      {editor.expanded && canEdit && setStartTime && setEndTime && (
        <li>
          <PartialRangeSlider
            editor={editor}
            duration={duration}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
          />
        </li>
      )}
    </Fragment>
  )
}

export function MemberInterestList({
  interestedMemberIds,
  goingMemberIds,
  tentativeIds = [],
  profiles,
  currentUserId,
  totems,
  setStartTime,
  setEndTime,
  onUpdatePartialRange,
  ownPartialRange,
  onUpdateOwnRange,
  memberRanges,
  checkedInIds,
  pregameGoerIds,
  tickets,
  onOpenTotem,
  onOpenMember,
  className,
}: Props) {
  const navigate = useTransitionNavigate()
  const favoriteIdSet = useFavoriteIdSet()

  const sort = (ids: string[]) =>
    [...ids].sort((a, b) => {
      if (currentUserId) {
        if (a === currentUserId) return -1
        if (b === currentUserId) return 1
      }
      // Checked in beats favorite: they're the people actually at the stage.
      const hereDelta = Number(!!checkedInIds?.has(b)) - Number(!!checkedInIds?.has(a))
      if (hereDelta !== 0) return hereDelta
      const favDelta = Number(favoriteIdSet.has(b)) - Number(favoriteIdSet.has(a))
      return favDelta !== 0 ? favDelta : compareByDisplayName(a, b, profiles, currentUserId)
    })
  const tentativeSet = new Set(tentativeIds)
  const listed = [...goingMemberIds, ...tentativeIds, ...interestedMemberIds.filter(uid => !tentativeSet.has(uid))]
  const listedSet = new Set(listed)
  const pregameOnlyIds = [...(pregameGoerIds ?? [])].filter(uid => !listedSet.has(uid))
  const allIds = [
    ...sort(goingMemberIds),
    ...sort(tentativeIds),
    ...sort(interestedMemberIds.filter(uid => !tentativeSet.has(uid))),
    ...sort(pregameOnlyIds),
  ]
  if (allIds.length === 0 && (!totems || totems.length === 0)) return null

  return (
    <ul className={cn('flex flex-col gap-2', className)}>
      {totems?.map((t, i) => (
        <TotemRow
          key={i}
          totem={t}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          onUpdatePartialRange={onUpdatePartialRange}
          onOpenProfile={
            onOpenTotem && t.groupId && t.totemId
              ? () => onOpenTotem(t.groupId!, t.totemId!)
              : undefined
          }
        />
      ))}
      {allIds.map(uid => {
        const profile = profiles[uid]
        const displayName =
          profile?.displayName?.trim() ||
          fbt('Unknown', 'Fallback name when a user profile has no display name')
        const isYou = uid === currentUserId
        return (
          <MemberRow
            key={uid}
            uid={uid}
            displayName={displayName}
            profile={profile}
            status={goingMemberIds.includes(uid) ? 'going' : interestedMemberIds.includes(uid) ? 'interested' : null}
            isYou={isYou}
            favorite={favoriteIdSet.has(uid)}
            checkedIn={!!checkedInIds?.has(uid)}
            pregameGoer={!!pregameGoerIds?.has(uid)}
            ticket={tickets?.holderIds.has(uid) ? 'holder' : tickets?.pendingIds.has(uid) ? 'pending' : undefined}
            range={(isYou ? ownPartialRange : memberRanges?.[uid]) ?? undefined}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
            onUpdateOwnRange={isYou ? onUpdateOwnRange : undefined}
            onClick={() =>
              !isYou && onOpenMember ? onOpenMember(uid) : navigate(isYou ? '/profile' : `/users/${uid}`)
            }
          />
        )
      })}
    </ul>
  )
}
